
import { mapMutations, mapState, mapGetters } from "vuex";
import Vue from "vue";
// apis
import { PortalAdapterApi } from "@vacancorp/portal.adapter.api";
import { DataType } from "./@types/top-area-view";
import { fetchTopAreaPlaceList, fetchTopAreaCountryList } from "@/api/adapter.api";
// tools
import { setLocalStorage } from "@/util/utils";
import Vue2Filters from "vue2-filters";
import { parameterizedURLQuery, getPrefectureDisplayName } from "@/mixins/common-functions";
// component
import AreaViewRow from "@/components/area-view/area-view-row.vue";
import AreaViewHeader from "@/components/area-view/area-view-header.vue";
import LayoutIosDummy from "@/layouts/layout-ios-dummy.vue";
import MaterialIcons from "@/components/common/material-icons.vue";
import Spinner from "@/components/common/spinner.vue";
import AreaViewFooter from "@/components/area-view/area-view-footer.vue";

export default Vue.extend({
  name: "top-area-view",
  mixins: [Vue2Filters.mixin],
  components: {
    AreaViewRow,
    AreaViewHeader,
    LayoutIosDummy,
    MaterialIcons,
    Spinner,
    AreaViewFooter,
  },
  props: {
    isModal: Boolean,
    isFromFilterPage: Boolean,
  },
  data(): DataType {
    return {
      clickFlag: "row",
      displayStatus: "country",
      selectedAreaList: [],
      selectedCountryText: "",
      selectedPrefectureText: "",
      countryList: undefined,
      topAreaList: undefined,
      selectedArea: undefined,
      fromPath: "",
      gpsButtonStatus: "normal",
    };
  },
  watch: {
    topAreaList() {
      this.displayStatus = "prefecture";
    },
    $route(to, _) {
      if (to.hash === "") {
        this.displayStatus = "country";
      } else {
        const hashName = to.hash.split("#")[1];
        this.displayStatus = hashName;
      }
    },
    hasCurrentLocation(newValue: boolean) {
      this.gpsButtonStatus = "normal";
      if (newValue) {
        this.$store.commit("data/setSelectedTopPlace", {
          areaName: "current-place",
          displayText: this.$i18n.t("Nav.gpsBtn.title") as string,
          location: {
            ...this.location,
            lat: this.location.lat + 0.0000000001,
          },
          displayOrder: 1,
        });
        this.closedBtnClicked(true);
      } else {
        this.setShowDenyGpsAlertView(true);
      }
    },
  },
  computed: {
    ...mapState("data", [
      "hasCurrentLocation",
      "location",
      "selectedTopPlace",
      "selectedGenre",
      "selectedCountry",
      "selectedPrefecture",
    ]),
    ...mapState("ui", ["whichBtnClickedOnSearchAlertUI"]),
    ...mapGetters("ui", ["isMobile"]),
    showActiveGPSButton(): boolean {
      return this.hasCurrentLocation === true || this.hasCurrentLocation === undefined;
    },
    showSpinner(): boolean {
      return !this.isFromFilterPage && this.gpsButtonStatus === "loading";
    },
    headerTransition(): string {
      return this.clickFlag === "row" ? "slide-left-fade" : "slide-right-fade";
    },
    showPrefectureBackBtnTitle(): string {
      return this.selectedGenre === "polling-place"
        ? (this.$i18n.t("Back.toTop") as string)
        : (this.$i18n.t("TopAreaView.headerTitleCountry") as string);
    },
    showCountryTitle(): string {
      return this.$i18n.t("TopAreaView.headerTitlePrefecture") as string;
    },
    showPrefectureTitle(): string {
      return this.selectedPrefectureText;
    },
    showBackBtn(): boolean {
      return this.displayStatus !== "country" && this.selectedGenre !== "polling-place";
    },
  },
  created() {
    // assign to change map type
    if (this.whichBtnClickedOnSearchAlertUI) {
      this.setSelectedGenre(this.whichBtnClickedOnSearchAlertUI);
    }

    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const date = today.getDate();
    setLocalStorage("date-open-top-area", `${year}-${month}-${date}`);

    this.getCountryList();
  },

  async mounted() {
    // the menu DOM is opened when it was from top menu
    this.showMenuPanel(false);
    // it is property for changing to independent page
    this.setIsAreaViewModal(this.isModal);
    // check to switch to area view
    await this.autoChangeDisplayStatus();
  },

  beforeRouteEnter(_, from, next) {
    next((vm: any) => {
      vm.fromPath = from.path;
    });
  },

  beforeDestroy() {
    this.setIsAreaViewModal(false);
  },

  methods: {
    ...mapMutations("ui", [
      "setIsAreaViewModal",
      "setShowDenyGpsAlertView",
      "showMenuPanel",
      "setWhichBtnClickedOnSearchAlertUI",
    ]),
    ...mapMutations("data", ["setSelectedCountry", "setSelectedGenre"]),
    /**
     * @remarks auto change the view status
     * based on genre (selected genre)
     */
    async autoChangeDisplayStatus() {
      console.log(`auto change display status, ${this.whichBtnClickedOnSearchAlertUI}, ${this.selectedGenre}`);
      // default to open in prefecture view
      await this.getTopAreaListByCountryName("jp");
      this.displayStatus = "prefecture";
      // only move from opening from area filter button
      if (!this.whichBtnClickedOnSearchAlertUI) {
        const query = this.$route.query as { [Key: string]: string };
        if (this.selectedTopPlace.areaName !== "Default" && this.selectedTopPlace !== "current-place") {
          // when access from /area/areaName, there is no prefecture data
          if (this.selectedPrefecture === undefined || this.selectedPrefecture === "") {
            this.displayStatus = "prefecture";
            this.$router.push(`${this.$route.path}?${parameterizedURLQuery(query)}#prefecture`).catch(() => {});
            return;
          }
          this.selectedAreaList = this.createSelectedAreaList(this.selectedPrefecture);
          // to find displayText
          if (this.topAreaList === undefined) return;
          const name: string | undefined = getPrefectureDisplayName(this.selectedPrefecture, this.topAreaList);
          this.selectedPrefectureText = name === undefined ? "" : name;
          // move view
          setTimeout(() => {
            this.displayStatus = "area";
            this.$router.push(`${this.$route.path}?${parameterizedURLQuery(query)}#area`).catch(() => {});
          }, 400);
        }
      }
      // reset
      this.setWhichBtnClickedOnSearchAlertUI(undefined);
    },
    closedBtnClicked(reallySelected: boolean) {
      const genre = this.$route.query.genre;
      if (genre) {
        this.setSelectedGenre(genre);
      }
      // if it's opened from top-view
      const { areaMenu } = this.$route.query;
      console.log(`close area page areaMenu = ${areaMenu}`);
      if (areaMenu === "1") {
        this.$router.push("/").catch();
        return;
      }

      this.setIsAreaViewModal(false);
      this.$emit("closed-btn-clicked", reallySelected === undefined ? false : reallySelected);
    },
    checkGPSLocation() {
      this.$store.dispatch("data/checkGeolocation");
    },
    currentLocationClicked() {
      // GA, GTM tracking
      this.$gtag.event("click", {
        event_category: "Area view item",
        event_label: "GPS button",
        value: 1,
      });
      // remove areaMenu to prevent go back to TOP view
      const query = this.$route.query;
      if (query.areaMenu) {
        delete query.areaMenu;
      }
      this.$router.replace({ query }).catch(() => console.warn(`param areaMenu on url was removed`));

      this.gpsButtonStatus = "loading";
      const currentPlace = {
        areaName: "current-place",
        displayText: this.$i18n.t("Nav.gpsBtn.title") as string,
        location: this.location,
        displayOrder: 1,
      };
      if (this.isModal && this.isFromFilterPage) {
        this.$emit("top-area-selected", currentPlace);
        return;
      }
      console.log(`select from area button from map: ${this.hasCurrentLocation}`);
      if (this.hasCurrentLocation === true) {
        this.$router.push(`/map/${currentPlace.location.lat},${currentPlace.location.lng}?areaName=current-place`);
        this.closedBtnClicked(true);
      } else if (this.hasCurrentLocation === undefined) {
        this.checkGPSLocation();
      }
    },
    areaClicked(area: PortalAdapterApi.TopArea) {
      this.setIsAreaViewModal(false);
      this.$emit("closed-btn-clicked", true);
      if (this.isModal && this.isFromFilterPage) {
        this.$emit("top-area-selected", area);
        return;
      }
      // no need to setSelectedTopPlace here
      this.$router.push(`/area/${area.areaName}`);
    },
    getCountryList() {
      fetchTopAreaCountryList()
        .then((res: PortalAdapterApi.ResponseGetTopAreaCountryList) => {
          this.countryList = res;
        })
        .catch(() => {
          this.$store.commit("ui/setAlertView", {
            status: true,
            msg: this.$i18n.t("Alert.networkError"),
            btnTitle: this.$i18n.t("Alert.reloadBtn"),
          });
        });
    },
    async getTopAreaListByCountryName(name: string) {
      if (name === "tw") {
        this.$store.commit("data/setSelectedGenre", "restaurant");
      }
      const genre: string =
        this.selectedGenre !== "polling-place" &&
        this.selectedGenre !== "evacuation-center" &&
        this.selectedGenre !== "disaster-prevention"
          ? "normal"
          : this.selectedGenre;
      this.topAreaList = await fetchTopAreaPlaceList(name, genre).catch(() => {
        this.$store.commit("ui/setAlertView", {
          status: true,
          msg: this.$i18n.t("Alert.networkError"),
          btnTitle: this.$i18n.t("Alert.reloadBtn"),
        });
        return [];
      });
    },
    backBtnClicked() {
      this.clickFlag = "back";
      console.log(`--- backBtnClicked ${this.displayStatus}, ${this.selectedGenre}---`);
      const query = this.$route.query as { [Key: string]: string };
      this.$nextTick(() => {
        if (this.displayStatus === "prefecture") {
          if (this.selectedGenre === "polling-place") {
            this.$router.push("/").catch(() => {});
          } else {
            this.$router.push(`${this.$route.path}?${parameterizedURLQuery(query)}#country`).catch(() => {});
          }
        } else if (this.displayStatus === "area") {
          this.$router.push(`${this.$route.path}?${parameterizedURLQuery(query)}#prefecture`).catch(() => {});
        }
      });
    },
    countryClicked(countryName: string, displayText: string) {
      this.clickFlag = "row";
      console.log(`countryClicked: ${countryName}`);
      this.setSelectedCountry(countryName);
      this.$nextTick(() => {
        this.selectedCountryText = displayText;
        this.getTopAreaListByCountryName(countryName);
      });
      this.$router.push("#prefecture").catch(() => {});
    },
    prefectureClicked(prefectureName: string, displayText: string) {
      this.clickFlag = "row";
      this.$store.commit("data/setSelectedPrefecture", prefectureName);
      this.$nextTick(() => {
        this.selectedPrefectureText = displayText;
        this.selectedAreaList = this.createSelectedAreaList(prefectureName);
      });
      const query = this.$route.query as { [Key: string]: string };
      this.$router.push(`${this.$route.path}?${parameterizedURLQuery(query)}#area`).catch(() => {});
    },
    createSelectedAreaList(selectedPrefecture: string): PortalAdapterApi.TopArea[] {
      if (this.topAreaList === undefined) return [];
      const result: PortalAdapterApi.TopArea[][] = this.topAreaList
        .filter((topArea) => topArea.areaGroupName === selectedPrefecture)
        .map((area) => area.areaList);

      return result[0];
    },
  },
});
